import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { forEach } from 'lodash';
import { ActivationEnd, Router } from '@angular/router';
import {
  UserAuth,
  ValuesExchangeProvider,
  LayoutProviderService,
  DialogService
} from '@nrwl/common-lib';
import { filter } from 'rxjs/operators';
import { FusionSidebarService } from './fusion-ui-sidebar.service';
import * as moment from 'moment';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { Subject } from 'rxjs';
import { SidebarCategoryItem, SidebarSubcategoryItem } from './fusion-ui-sidebar.interfaces';
import { MonitorsService } from '@nrwl/monitor-management';

@Component({
  selector: 'fusion-ui-sidebar',
  templateUrl: './fusion-ui-sidebar.template.html',
  styleUrls: ['./fusion-ui-sidebar.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * @property {@Input} dataSource - Might be deprecated
 * @property {@Input} wizardData - Might be deprecated
 * @property {Subject} _unsubscribe - Used for unsubscription
 * @property {boolean} _isWizardOpened - The state of the wizard (opened|closed)
 * @property {SidebarCategoryItem} listItems - Contains the list with all items (categories and subcategories)
 * @property {SidebarSubcategoryItem} menuList - Contains the list with subcategories when the wizard modal is open
 * @property {boolean} disableItem - Might be deprecated
 * @property {string} displayMenu - A flag representing the opened subcategory in the qizard
 * @property {string} selectedId - Represents the flag of the current selected category
 * @property {string} subSelectedId - Represents the flag of the current selected subcategory
 */
export class SideBarNavComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() dataSource: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() wizardData: any;
  @ViewChild('sideWeb') element: ElementRef;
  public listItems: SidebarCategoryItem[] = [];
  public menuList: SidebarSubcategoryItem[] = [];
  public disableItem: boolean;
  public displayMenu: string | null = null;
  public selectedId: string;
  public subSelectedId: string;
  private readonly _unsubscribe: Subject<void> = new Subject();
  private _isWizardOpened = false;

  constructor(
    private readonly layoutService: LayoutProviderService,
    private readonly userAuth: UserAuth,
    private readonly router: Router,
    private readonly exchange: ValuesExchangeProvider,
    private readonly fusionSidebarService: FusionSidebarService,
    private readonly navigator: NavigationProvider,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly monitorsService: MonitorsService,
    private readonly dialogService: DialogService
  ) {
    this.listItems = this.fusionSidebarService.prepareListItems();
    this._activeItemsRouterSubscription();
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  @HostListener('document:click', ['$event']) onClick(event: Event) {
    if (!this.element.nativeElement.contains(event.target)) {
      this.displayMenu = null;
    }
  }
  ngOnInit() {
    this.exchange.registerInterest('on_close', this._onCloseWizardListener.bind(this));
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  /**
   * Check if an item from sidebar passes the authentication
   * @param {SidebarCategoryItem} item - A sidebar category item
   */
  public hasAuth(item: SidebarCategoryItem): boolean {
    return item.auth ? item.auth.some(item => this.userAuth.isAuthedFor(item)) : true;
  }

  /**
   * Opens the subcategories modal
   * @param {SidebarCategoryItem} item - A sidebar category item
   */
  public openRightPanel(item: SidebarCategoryItem): void {
    this.displayMenu = item.typeId;
    if (item.typeId === 'wizards') {
      this._navigateToWizard(item.routeId);
    } else {
      // we can trigger immediate navigation by simply checking whether there isn't any submenu
      this.displayMenu = null;
      item.outlet ? this._navigateByOutletParams(item) : this._navigateByTypeOnly(item);
    }
  }

  /**
   * Closes the subcategories modal
   */
  public closeRightPanel(): void {
    this.displayMenu = null;
  }

  /**
   * Navigating while clicking a subcategory
   * @param {SidebarSubcategoryItem} item - A sidebar subcategory item
   */
  public onNavigationBtnClick(item: SidebarSubcategoryItem): void {
    this.displayMenu = null;

    if (item.type === 'wizards') {
      this._navigateToWizard(item.typeId);
    } else {
      item.outlet ? this._navigateByOutletParams(item) : this._navigateByTypeOnly(item);
    }
  }

  showTestWarningMessage(maxLimit: number): void {
    const param = this.monitorsService.getCreateTestWarningMsg(maxLimit);
    this.dialogService.showDialog('confirm', 'body', param);
  }

  /**
   * Subscribe to router ActivationEnd event to mark categories and subcategories in sidebar.
   */
  private _activeItemsRouterSubscription(): void {
    this.router.events
      .pipe(filter(event => event instanceof ActivationEnd))
      .subscribe(() => this._selectSidebarItems());
  }

  /**
   * Select items in sidebar (categories and subcategories) based on events
   */
  private _selectSidebarItems() {
    const { categoryId, subcategoryId } = this.fusionSidebarService.getSelectedIds(
      this.listItems,
      this.router.routerState.snapshot.url
    );

    this.selectedId = categoryId ? categoryId : this.selectedId;
    this.subSelectedId = subcategoryId;

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Navigate using router by Type
   * @param {SidebarCategoryItem | SidebarSubcategoryItem} item - An item from sidebar (category or subcategory)
   */
  private _navigateByTypeOnly(item: SidebarCategoryItem | SidebarSubcategoryItem): void {
    this.navigator.navigateByType(item);
  }

  /**
   * Navigate using the router by Outlet
   * @param {SidebarCategoryItem | SidebarSubcategoryItem} item - An item from sidebar (category or subcategory)
   */
  private _navigateByOutletParams(item: SidebarCategoryItem | SidebarSubcategoryItem): void {
    this.navigator.navigateByOutletParams(item);
  }

  /**
   * On Close Wizard Modal Event
   * @param {any} avent - This varies based on the caller
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  private _onCloseWizardListener(event: any): void {
    if (!event.wizardId) {
      this._isWizardOpened = false;
    }
  }

  /**
   * Navigate to wizard
   * @todo: (Cristian): navigation methods should be moved into a service for everyone to call them programmatically
   * if they need (not to implement them every time, in every element that needs them)
   * @param {string} wizardId - A wizard flag indicator
   */
  private _navigateToWizard(wizardId: string): void {
    if (this._isWizardOpened) {
      this.exchange.notifyListeners('route_guard', {
        wizardId,
        id: this.layoutService.getCurrentPerspective(),
        timestamp: moment().unix()
      });
    } else {
      this.navigator.addRootParam('fuzzy_filter', { expression: [] }).navigateToWizard([wizardId]);
    }

    this._isWizardOpened = true;
  }
}
